import { useContext, useDebugValue } from 'react';
import {
  IActiveDatabaseContext,
  ActiveDatabaseContext,
  IRoutineAvailability,
  IAvailableRoutine,
} from './ActiveDatabaseContext';
import { KeyValuePair, fromPairs } from 'ramda';
import { System } from '../../Features/Routing/Utils/routerUtils';
import DelsystemLista, {
  IDelsystem,
} from '../../Features/Delsystem/_Common/Const/Delsystem';
import {
  rutinInfos,
  rutinKeys,
} from '../../Features/Delsystem/_Common/Const/RutinInfo';
import { ActiveDatabaseInfoQuery } from './_QueryTypes/ActiveDatabaseInfoQuery';
import { ApolloError } from '@apollo/client';

export const databaseMissingErrorText = 'Korrekt databas saknas';

export const useActiveDatabaseContext = () => {
  const context = useContext<IActiveDatabaseContext>(ActiveDatabaseContext);
  useDebugValue(context, context => context.activeDatabase);
  return context;
};

const rutinIdToKeyValue = (
  rutinId: number,
  readonly: boolean
): KeyValuePair<number, IAvailableRoutine> => {
  return [
    rutinId,
    {
      ...rutinInfos[rutinKeys[rutinId]],
      readonly: readonly,
    },
  ];
};

const rutinIdListToAvailability = (
  rutinIds: number[],
  readonlyRutinIds: number[]
): IRoutineAvailability =>
  fromPairs(
    rutinIds
      .map(x => rutinIdToKeyValue(x, false))
      .concat(readonlyRutinIds.map(x => rutinIdToKeyValue(x, true)))
  );

// Alla delsystem där man är behörig till någon rutin eller styrvärde
const getAvailableDelsystem = (
  availableRoutinesForUser: number[]
): IDelsystem[] => {
  return DelsystemLista.filter(
    ds =>
      ds.rutiner.some(rutin =>
        availableRoutinesForUser.includes(rutin.rutinID)
      ) ||
      ds.styrvarden.some(rutin =>
        availableRoutinesForUser.includes(rutin.rutinID)
      )
  );
};

const extractRoutinesFromMeData = (
  data: ActiveDatabaseInfoQuery | null | undefined,
  activeDatabase: string | null
): { allowed: number[]; readonly: number[] } => {
  const maybeAllowedRoutines = data?.me?.[0]?.allowedroutines?.find(
    db => db.dbname === activeDatabase
  );

  return {
    allowed:
      maybeAllowedRoutines?.allowedroutines?.filter(v => v !== undefined) ?? [],
    readonly: maybeAllowedRoutines?.readonlyroutines?.filter(v => !!v) ?? [],
  };
};

export const createDatabaseContextValue = (
  activeDatabase: string | null,
  data: ActiveDatabaseInfoQuery | undefined,
  loading: boolean,
  error: ApolloError | undefined,
  skipLoadInfo: boolean,
  system: System,
  userID: string | null
): IActiveDatabaseContext => {
  const { allowed, readonly } = extractRoutinesFromMeData(data, activeDatabase);

  const availableRoutines = rutinIdListToAvailability(allowed, readonly);

  const availableDelsystem = getAvailableDelsystem(
    Object.values(availableRoutines).map(routine => routine.rutinID)
  );

  return {
    activeDatabase,
    activeDatabaseInfo: data,
    activeDatabaseInfoLoading:
      loading || skipLoadInfo || (data?.me == null && !error),
    activeDatabaseInfoError: error,
    availableRoutines,
    availableDelsystem,
    system,
    userID,
  };
};
